<template>
  <div id="app">
    <!-- Page autonome (403…) : affichée seule, sans menu ni header, connecté ou non -->
    <div
      v-if="$route.meta.standalone"
      id="app_content_standalone"
    >
      <router-view />
    </div>
    <!-- Affiche un loader pendant l'initialisation, la vérification de session ou les redirections -->
    <div
      v-else-if="showAppLoader"
      id="app_loader"
    >
      <InfiniteLoader
        v-if="!identifyFailed"
        :height="64"
        :width="64"
      />
      <div
        v-else
        class="error-identify"
      >
        <img
          src="@lde/core_lde_vue/dist/assets/media/logos/logo_cristal.svg"
          alt="Logo Cristal"
        />
        {{ $t("general.une-erreur-sest-produite-reessayer") }}
        <br />
        {{ $t("general.si-probleme-persiste-contacter-lde-mail") }}
      </div>
    </div>
    <div
      v-else-if="$route.meta.accessNotLogged && !isLogged && (!$route.meta?.cataloguePublic || !isCataloguePublicActif)"
      id="app_content_not_logged"
    >
      <router-view />
    </div>
    <div
      v-else-if="!showSessionPublicWarning"
      id="app_content"
    >
      <div id="sidebar">
        <section
          id="menu"
          :class="{ 'dark-mode': darkMode, }"
        >
          <div>
            <NavigationMenu
              :expanded-menu="expandedSidebar"
              :dark-mode="darkMode"
              :menu="forbiddenMode || askChooseOrg ? [] : menuToShow"
              :show-home-button="isNormandie && !askChooseOrg"
            >
              <template
                v-if="!askChooseOrg && currentLogo"
                #logo
              >
                <router-link
                  :to="{ name: 'home', }"
                  :title="$t('404.page-accueil')"
                >
                  <figure id="logos_marche">
                    <img
                      id="logo_maitre_compta"
                      :src="currentLogo"
                      :alt="`${$t('general.alt-logo')} ${organismeActuel.nom}`"
                    />
                  </figure>
                </router-link>
              </template>
            </NavigationMenu>
            <div
              v-if="modeAjoutReference"
              id="info_devis_ajout_reference"
            >
              <div class="icons">
                <IconFileDevis />
                <UilEditAlt />
              </div>
              <p>
                {{ $t("devis.vous-modifier-devis", {
                  id: devisAjoutReference.id_4d,
                  libelle: devisAjoutReference.libelle,
                }) }}
              </p>

              <p>
                {{ $t("devis.vous-pouvez-ajouter-des-refs") }}
              </p>

              <div
                v-if="$route.name !== 'listes_devis_devis_item'"
                class="prices"
              >
                <strong
                  v-if="nouveauPrixDevisModif.ttc !== nouveauPrixDevisModif.ht"
                  class="all-taxes"
                  v-html="$t('prix.ttc', { prix: moneyFilter(nouveauPrixDevisModif.ttc), })"
                />
                <span
                  class="light duty-free"
                  v-html="$t('prix.ht', { prix: moneyFilter(nouveauPrixDevisModif.ht), })"
                />
              </div>

              <div>
                <p
                  v-if="referencesAjouteesAuDevis.length"
                  class="italic"
                >
                  {{ $t("devis.refs-en-attente-enregistrement", {
                    count: referencesAjouteesAuDevis.length,
                  }) }}
                </p>
                <ButtonClassic
                  v-if="$route.name !== 'listes_devis_devis_item'"
                  color="primary"
                  :label="$t('devis.finaliser-la-modification')"
                  variant="solid"
                  @click="$router.push({
                    name: 'listes_devis_devis_item',
                    params: { id: devisAjoutReference.id, },
                    query: { 'ajout-ref-save': true, },
                  })"
                />
              </div>
            </div>
          </div>
          <template v-if="false">
            <ButtonClassic
              id="dark_mode"
              :class="{ reduce: !expandedSidebar, }"
              :label="labelDarkMode"
              variant="special"
              :color="darkMode ? 'white' : 'primary'"
              icon="right"
              size="small"
              @mouseover="hoveredDarkMode = true"
              @mouseleave="hoveredDarkMode = false"
              @click="darkMode = !darkMode"
            >
              <template #right-icon>
                <UilSun v-if="darkMode" />
                <UilMoon v-else />
              </template>
            </ButtonClassic>
          </template>
        </section>
        <footer
          v-if="askChooseOrg || !currentLogo"
          id="assistance"
        >
          <ButtonClassic
            v-if="showAssistanceButton"
            v-matomo-log-click="['assistance_footer', ]"
            v-tooltip="mailTooltipAttr()"
            v-bind="btnAssistanceAttr"
            variant="special"
            color="white"
            icon="left"
            @click="copyMailLde()"
          >
            <template #left-icon>
              <UilQuestionCircle />
            </template>
          </ButtonClassic>
        </footer>
        <footer
          v-else-if="currentLogo"
          id="logo_secondary"
        >
          <router-link
            :to="{ name: 'home', }"
            :title="$t('404.page-accueil')"
          >
            <figure id="logos">
              <img
                id="logo_cristal"
                src="@lde/core_lde_vue/dist/assets/media/logos/logo_cristal_white.svg"
                :alt="$t('general.alt-logo-cristal')"
              />
              <figcaption
                v-if="expandedSidebar"
                class="s4 bold"
              >
                Cristal
              </figcaption>
              <div v-if="expandedSidebar">
                <img
                  id="logo_lde"
                  src="@lde/core_lde_vue/dist/assets/media/logos/logo_lde_white.svg"
                  :alt="$t('general.logo-lde')"
                />
              </div>
            </figure>
          </router-link>
        </footer>
        <footer v-else />
      </div>
      <div id="wrapper">
        <div
          v-if="impersonator"
          id="impersonating"
          class="text-small"
        >
          {{ $t("general.vous-etes-connectes-en-tant-que-x", { nom: `${user.contact.prenom } ${user.contact.nom}`, }) }}
          <a :href="impersonateStopUrl">
            {{ $t("general.revenir-a-x", { nom: `${impersonator.contact.prenom } ${impersonator.contact.nom}`, }) }}
          </a>
        </div>
        <HeaderBloc
          :dark-mode="darkMode"
          :disabled="forbiddenMode || askChooseOrg"
          :expanded-sidebar="internalExpandedSidebar"
          @change-expanded-sidebar="internalExpandedSidebar = $event"
        />
        <main
          id="scroll_content"
          data-bs-spy="scroll"
          data-bs-target="#nav_titles"
          tabindex="0"
          @scroll="toggleHeaderScroll"
        >
          <AccessProvider v-if="askLinkAccount" />
          <MultipleAccounts v-else-if="askChooseOrg" />
          <ErrorPage
            v-else-if="goToErrorPage"
            :variant="errorVariant"
          />
          <template v-else>
            <router-view :key="$route.path" />
            <ModalVerifyAccount
              v-if="isLogged"
            />
          </template>
          <span id="custom_radius" />
          <MainFooter>
            <template #left-links>
              <a
                :href="config.url.mentionsLegales"
                target="_blank"
                rel="noreferrer noopener"
              >
                {{ $t('footer.mentions-legales') }}
              </a>
              <a
                :href="config.url.cgu"
                target="_blank"
                rel="noreferrer noopener"
              >
                {{ $t('footer.cgu') }}
              </a>
              <a
                :href="config.url.rgpd"
                target="_blank"
                rel="noreferrer noopener"
              >
                {{ $t('footer.rgpd') }}
              </a>
              <button
                class="button"
                @click="showCookies = true"
              >
                {{ $t('footer.cookies') }}
              </button>
            </template>
          </MainFooter>
        </main>
      </div>
      <transition name="slide">
        <CookiesControl
          v-if="!user.cookies || !user.cookies.date || showCookies"
          @save="setCookiesFeatures($event)"
        />
      </transition>
      <!-- Catalogue public : choix du pays du visiteur (ouverture automatique tant qu'il n'est pas choisi) -->
      <ModalChoixPaysCataloguePublic v-if="!isLogged && isCataloguePublicActif" />
    </div>
    <!-- Modale bloquante plein écran : connecté sur le domaine public ou connexion détectée depuis un autre onglet -->
    <ModalSessionPublicWarning
      v-if="showSessionPublicWarning"
    />
    <!-- Modale superposée : la page reste montée derrière pour ne rien perdre du travail en cours -->
    <ModalSessionExpiree
      v-if="(sessionExpiree || avertissementSessionAffiche) && !showSessionPublicWarning && !$route.meta.standalone"
    />
    <SurveillanceSession
      v-if="isLogged && !sessionExpiree && !showSessionPublicWarning && !$route.meta.standalone"
    />
  </div>
</template>

<script>
import { mapGetters } from "vuex";

import {
  ButtonClassic,
  InfiniteLoader,
  MainFooter,
  moneyFilter,
  NavigationMenu,
  useScreenWidth,
} from "@lde/core_lde_vue";
import {
  UilMoon, UilSun, UilEditAlt, UilQuestionCircle,
} from "@lde/vue-unicons";

import ErrorPage from "@/components/layout/ErrorPage.vue";
import AccessProvider from "@/components/layout/AccessProvider.vue";
import HeaderBloc from "@/components/layout/HeaderBloc.vue";
import CookiesControl from "@/components/CookiesControl.vue";
import MultipleAccounts from "@/components/layout/MultipleAccounts.vue";
import ModalVerifyAccount from "@/components/modals/ModalVerifyAccount.vue";
import SurveillanceSession from "@/components/layout/SurveillanceSession.vue";
import ModalSessionExpiree from "@/components/modals/ModalSessionExpiree.vue";
import ModalSessionPublicWarning from "@/components/modals/ModalSessionPublicWarning.vue";
import ModalChoixPaysCataloguePublic from "@/components/modals/ModalChoixPaysCataloguePublic.vue";
import config from "@/config";
import Sentry from "@/modules/sentry";
import { changePageTitle } from "@/modules/utils";
import menu, { buildCataloguesSubmenu } from "@/router/menu";
import mixinOpenAdmin from "@/mixins/mixinOpenAdmin";
import mixinAssistance from "@/mixins/mixinAssistance";
import { getMainLogo } from "@/composables/logo";

import IconFileDevis from "./components/icons/IconFileDevis.vue";

/**
 * Définit la structure de base du template du site.
 */
export default {
  name: "App",
  components: {
    HeaderBloc,
    NavigationMenu,
    ButtonClassic,
    MainFooter,
    ErrorPage,
    AccessProvider,
    InfiniteLoader,
    CookiesControl,
    ModalChoixPaysCataloguePublic,
    MultipleAccounts,
    ModalVerifyAccount,
    SurveillanceSession,
    ModalSessionExpiree,
    ModalSessionPublicWarning,
    UilMoon,
    UilSun,
    UilQuestionCircle,
    IconFileDevis,
    UilEditAlt,
  },
  mixins: [
    mixinOpenAdmin,
    mixinAssistance,
  ],
  provide() {
    return {
      $t: this.$t.bind(this),
      expandedSidebar: () => this.expandedSidebar,
    };
  },
  setup() {
    const { isMedium } = useScreenWidth();
    return { isMedium };
  },
  data() {
    return {
      menu,
      showCookies: false,
      internalExpandedSidebar: true,
      hoveredDarkMode: false,
      authorizedRoutes: ["mentions_legales", "cgu", "rgpd"],
      isLoading: false,
      sessionDetectedInOtherTab: false,
    };
  },
  computed: {
    config() {
      return config;
    },
    ...mapGetters([
      "accessForbidden",
      "activeCataloguesForMenu",
      "askChooseOrg",
      "askLinkAccount",
      "devisAjoutReference",
      "errorStatus",
      "hasPerm",
      "hasPerms",
      "identifyFailed",
      "impersonator",
      "isAURA",
      "isGE",
      "isGMBH",
      "isHorsMarche",
      "isLogged",
      "isInitialCheckDone",
      "isRedirecting",
      "isOnPublicDomain",
      "isMaitreComptaRegion",
      "isNormandie",
      "modeAjoutReference",
      "nouveauPrixDevisModif",
      "organismeActuel",
      "preferences",
      "referencesAjouteesAuDevis",
      "urlCristalWeb",
      "urlHotline",
      "user",
      "isCataloguePublicActif",
      "cataloguePublicSlug",
      "sessionConflict",
      "sessionExpiree",
      "avertissementSessionAffiche",
    ]),
    showAppLoader() {
      const { meta, name } = this.$route;
      if (name === null) return true;
      if (this.isRedirecting || !this.isInitialCheckDone) return true;
      return !this.isLogged && !meta?.accessNotLogged;
    },
    showAssistanceButton() {
      return !this.isGE && !this.isAURA;
    },
    /**
     * Affiche la modale bloquante si :
     * - l'utilisateur est connecté ET sur le domaine public, OU
     * - une connexion a été détectée depuis un autre onglet (même origine via localStorage), OU
     * - un conflit de session a été détecté lors d'un appel panier (cross-subdomain).
     */
    showSessionPublicWarning() {
      if (this.showAppLoader || !this.isOnPublicDomain) return false;
      return this.isLogged
        || this.sessionDetectedInOtherTab
        || this.sessionConflict;
    },
    expandedSidebar() {
      return this.forbiddenMode || this.askChooseOrg ? false : this.internalExpandedSidebar;
    },
    /**
     * Retourne le logo du marché (collapsed ou full selon la sidebar),
     * ou null si hors marché.
     */
    currentLogo() {
      return getMainLogo(!this.expandedSidebar);
    },
    forbiddenMode() {
      return this.accessForbidden || this.askLinkAccount;
    },
    darkMode: {
      get() {
        return !!this.preferences?.darkMode;
      },
      set(val) {
        this.$store.commit("updatePreferences", { darkMode: val });
      },
    },
    labelDarkMode() {
      if (this.hoveredDarkMode && this.expandedSidebar) {
        return this.darkMode ? this.$t("general.mode-jour") : this.$t("general.mode-nuit");
      }
      return null;
    },
    goToErrorPage() {
      return !this.authorizedRoutes.includes(this.$route.name) && (this.forbiddenMode || this.errorStatus);
    },
    errorVariant() {
      if (this.$route.name === "404" || this.errorStatus === 404) {
        return "404";
      }
      if (this.errorStatus === 403) {
        return "403";
      }
      return "site-forbidden";
    },
    isFactureVisible() {
      return this.hasPerm("view_facture");
    },
    menuToShow() {
      const activeCatalogues = this.activeCataloguesForMenu;

      let menuToShow = [...this.menu];

      menuToShow = menuToShow
        .filter((m) => this.filterMenu(m))
        .map((m) => ({
          ...m,
          title: this.getTitle(m),
          submenu: m.submenu
            .filter((sm) => this.filterMenu(sm))
            .map((sm) => ({
              ...sm,
              // Définit le lien vers le serveur CW
              extLink: sm.name === "cristal_web" && this.hasPerm("use_cristalweb") ? this.urlCristalWeb : undefined,
            })),
        }))
        .map((m) => {
          if (m.name !== "catalogues") {
            return m;
          }

          return {
            ...m,
            submenu: buildCataloguesSubmenu(m.submenu, activeCatalogues),
          };
        });

      menuToShow = menuToShow.filter((m) => !m.submenu || m.submenu.length > 0);

      // Ajout du bouton assistance
      // Double sécurité sur la condition isGE (en plus du filterMenu)
      // Et sécurité pour éviter les doublons.
      if (
        this.currentLogo
        && this.showAssistanceButton
        && !menuToShow.some((m) => m.name === "assistance")
      ) {
        menuToShow.push(
          {
            name: "assistance",
            title: () => this.$t("general.assistance"),
            icon: UilQuestionCircle,
            submenu: [],
            extLink: this.isGMBH ? `mailto:${config.mails.lde}` : this.urlHotline,
          },
        );
      }

      if (this.modeAjoutReference) {
        const limitedMenu = [
          "livres_manuels_papier",
          "ressources_numeriques",
          "fournitures_papeterie",
        ];

        menuToShow.forEach((m) => {
          m.submenu = m.submenu.filter((sm) => limitedMenu.some((menuName) => sm.name.startsWith(menuName)));
          return m;
        });
        menuToShow = menuToShow.filter((m) => m.submenu.length > 0);
      }
      return menuToShow;
    },
    impersonateStopUrl() {
      return `${config.backUrl}/impersonate/stop`;
    },
  },
  watch: {
    $route: {
      // Réinitialise l'erreur pour permettre à nouveau la navigation
      handler(route, oldRoute) {
        if (route.path !== oldRoute.path || JSON.stringify(route.query) !== JSON.stringify(oldRoute.query)) {
          this.$store.commit("setErrorStatus", null);
        }

        if (route.name === "home") {
          if (this.modeAjoutReference) {
            const firstSubmenu = this.menuToShow[0]?.submenu?.[0];
            if (firstSubmenu?.extLink) {
              this.$router.replace(firstSubmenu.extLink);
              return;
            }
            const fallbackRouteName = `${this.menuToShow[0].name}_${this.menuToShow[0].submenu[0].name}`;
            this.$router.replace(
              firstSubmenu?.routerLinkObj || { name: fallbackRouteName },
            );
          }
        }
      },
    },
    "$route.meta.title": {
      handler(title) {
        if (title) {
          // Permet de changer le document.title et donc l'historique du navigateur
          changePageTitle(title(), this.$route, true);
        }
      },
      deep: true,
      immediate: true,
    },
    isMedium(val) {
      this.internalExpandedSidebar = !val;
    },
  },
  mounted() {
    this.internalExpandedSidebar = !this.isMedium;

    // Détecte une connexion depuis un autre onglet (même origine) sans appel réseau
    this.onStorageChange = (e) => {
      if (e.key === "session_event" && this.isOnPublicDomain && !this.isLogged) {
        this.sessionDetectedInOtherTab = true;
      }
    };
    window.addEventListener("storage", this.onStorageChange);
  },
  beforeUnmount() {
    clearTimeout(this.hidePopoverTimer);
    if (this.onStorageChange) {
      window.removeEventListener("storage", this.onStorageChange);
    }
  },
  methods: {
    moneyFilter,
    /**
     * Copie un texte dans le presse-papiers.
     * @param {String} text Texte à copier.
     * @param {String} title Titre du toast à afficher.
     */
    copyToClipboard(text, title = null) {
      const el = document.createElement("textarea");
      el.value = text;
      document.body.appendChild(el);
      el.select();
      document.execCommand("copy");
      document.body.removeChild(el);

      this.$toast.success({
        title: title || this.$t("toast.element-copie-presse-papier"),
      });
    },
    /**
     * Définit les fonctionnalités à activer ou non selon les cookies et ferme le bandeau avec une animation.
     * @param {Object} cookies Choix de l'utilisateur sur les cookies.
     */
    setCookiesFeatures(cookies) {
      if (cookies.technique) {
        Sentry.init();
        Sentry.setScope();
      } else {
        Sentry.unsetScope();
        Sentry.flush();
      }

      window._paq.push([cookies.audience ? "setConsentGiven" : "forgetConsentGiven"]);
      this.showCookies = false;
    },
    /**
     * Affiche le header si on scroll vers le bas. Le cache si on scroll vers le haut.
     * Adapte aussi la hauteur du #aside_content.
     * @param {Object} event Événement natif du scroll.
     */
    toggleHeaderScroll(event) {
      const currentScrollTop = event.target.scrollTop;
      const pageHeader = document.querySelector("#page_header");

      if (pageHeader) {
        // Décale le header de sa propre taille (la hauteur peut varier selon le contenu) quand on scrolle vers le bas
        // Reset la position du header si on scrolle vers le haut
        pageHeader.style.top = currentScrollTop >= this.prevScrollPos ? `-${pageHeader.offsetHeight}px` : 0;
        this.prevScrollPos = currentScrollTop;
      }
    },
    /**
     * Change le titre du menu selon la permission.
     * @param {Object} link Menu courant.
     */
    getTitle({ name, title }) {
      // Si on peut voir les factures, on change le titre du menu.
      if (name === "commandes_factures" && this.isFactureVisible) {
        return () => this.$t("recherche.commandes-et-factures");
      }

      if (name === "listes_devis" && this.hasPerm("view_devis")) {
        return () => this.$t("menu.listes-et-devis");
      }

      // Si on est maître compta, on met au pluriel le titre des établissements
      if (name === "etablissements" && this.isMaitreComptaRegion) {
        return () => this.$t("menu.mes-etablissements", 2);
      }

      return title;
    },
    /**
     * Filtre le menu.
     * @param {Object}
     */
    filterMenu({ permissions, name }) {
      switch (name) {
        case "factures": {
          return this.isFactureVisible;
        }
        case "assistance": {
          return this.showAssistanceButton;
        }
        case "suivi_demandes_licences": {
          return this.user?.role === "Opérateur LDE";
        }
        default: {
          break;
        }
      }

      if (!this.isLogged) {
        // Flatten menu and submenus for cataloguePublic check
        const flatMenu = this.menu.flatMap((m) => [
          m,
          ...(m.submenu || []),
        ]);
        return (
          flatMenu.some((item) => item.name === name && item.cataloguePublic === true)
          && this.isCataloguePublicActif
        );
      }

      return this.hasPerms(permissions);
    },
  },
};
</script>

<style lang="scss">
@use "@/assets/styles/app.scss";
@use "@lde/core_lde_vue/dist/assets/styles/_variables.scss" as var;

.error-identify {
  display: flex;
  text-align: center;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: var.$space-small;
  font-size: var.$font-regular;
  img {
    width: var.$space-x-large;
    height: var.$space-x-large;
  }
}
</style>
